/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import get from 'lodash/get';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { Text } from 'office-ui-fabric-react/lib/Text';
import useAPI from '../../hooks/useAPI';
import { fetchUsers } from '../../actions/users';
import UserList from '../../components/UserList';
import theme from '../../theme.style';
import style from './SearchUsers.style.v2';
import Pagination from '../../components/Pagination';
import Module from '../../components/Module';
import SearchBar from '../../components/SearchBar';
import { search } from '../../actions/search';

const SearchUsers = () => {
  const pageLimit = 500;
  const query = useLocation()?.search;
  const history = useHistory();
  const searchParams = new URLSearchParams(query);
  const role = searchParams.get('role');
  const selectedPartnerRole = searchParams.get('selectedPartnerRole');
  const selectedPartnerName = searchParams.get('selectedPartnerName');
  const searchedEmailId = searchParams.get('searchedEmailId');

  const [showPartnerSuggestions, setShowPartnerSuggestions] = useState(true);
  const [resetPagination, setResetPagination] = useState(false);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [emailQuery, setEmailQuery] = useState(searchedEmailId || '');
  const [filterState, setFilterState] = useState({
    role: role || 'msft',
    count: pageLimit,
    ...(selectedPartnerRole && { secondaryRoles: [selectedPartnerRole] }),
  });
  const [searchFilter, setSearchFilter] = useState(searchedEmailId || !role ? 'emailId' : role);
  const { isLoading: isUsersLoading } = useAPI(() => fetchUsers(filterState), [filterState]);
  const { data: suggestions, isLoading } = useAPI(
    () => search(['partnerUsers'], partnerQuery),
    [partnerQuery],
    {
      fetchOnMount: true,
      autoFetch: true,
    },
  );

  const usersBasedOnRole = filterState.role === 'partner' ? 'partnersByEmail' : 'users';
  const { data: userResults, isLoading: isSearchResultsLoading } = useAPI(
    () => search([usersBasedOnRole], emailQuery),
    [emailQuery],
    {
      fetchOnMount: false,
      autoFetch: true,
    },
  );
  const [selectedPartner, setSelectedPartner] = useState(selectedPartnerName || '');
  const partnerSuggestions = get(suggestions, 'partnerUsers', [])
    .map(partner => ({
      icon: 'People',
      value: partner.partnerRole,
      label: `${partner.name} (${partner.partnerRole})`,
    }))
    .slice(0, 5);

  const searchResults = get(userResults, 'users', []).map(userResult => ({
    ...userResult,
    key: userResult.oid,
    displayName: userResult.displayName,
    role: userResult.role,
    mail: userResult.mail,
    secondaryRoles: userResult.secondaryRoles?.map(r => r.name).join(', '),
  }));

  const totalCount = useSelector(state => get(state, 'pagination.users.totalCount', 0));
  const users = useSelector(state =>
    Object.values(get(state, `entities.users`, {})).filter(u => u.role === filterState.role),
  ).map(user => ({
    ...user,
    key: user.oid,
    displayName: user.displayName,
    role: user.role,
    mail: user.mail,
    secondaryRoles: user.secondaryRoles?.map(r => r.name).join(', '),
  }));

  const userFilterOptions = [
    {
      key: 0,
      text: 'Microsoft',
      role: 'msft',
      noResultMessage: 'Try searching by User Email Id',
      ...((!role || role === 'msft') && { selected: true }),
    },
    {
      key: 1,
      text: 'Partner',
      role: 'partner',
      noResultMessage: 'Try searching by User Email Id, Vendor Id, Contract Id, or Partner Name',
      ...(role === 'partner' && { selected: true }),
    },
  ];

  const searchOptions = [
    {
      key: 'partner',
      text: 'Partner/ContractId/VendorId',
      placeholder: 'Search by Partner/ContractId/VendorId',
    },
    {
      key: 'emailId',
      text: 'User Email',
      placeholder: 'Search by email Id',
    },
  ];

  const onRoleChange = async (event, option) => {
    const newFilterState = {
      role: option.role,
      start: 0,
      count: pageLimit,
    };
    setFilterState(newFilterState);
    setEmailQuery('');
    setPartnerQuery('');
    setSelectedPartner('');
    if (option?.role === 'partner') {
      setSearchFilter('partner');
    } else {
      setSearchFilter('emailId');
    }
    history.push(`/permissions/users?role=${option.role}`);
  };

  function updateHistoryWithSelection(selectedRole, selection, type) {
    if (type === 'emailId') {
      searchParams.set('searchedEmailId', selection.value);
    } else {
      searchParams.set('selectedPartnerRole', selection.value);
      searchParams.set('selectedPartnerName', selection.label);
    }
    searchParams.set('role', selectedRole);
    history.push({
      pathname: '/permissions/users',
      search: searchParams.toString(),
    });
  }

  const handleSearch = newInput => {
    if (searchFilter === 'emailId') {
      setPartnerQuery('');
      setEmailQuery(newInput);
      updateHistoryWithSelection(filterState.role, { value: newInput }, 'emailId');
      setShowPartnerSuggestions(false);
    } else {
      setEmailQuery('');
      setPartnerQuery(newInput);
      setShowPartnerSuggestions(true);
    }
  };

  const handlePageChange = paginationData => {
    setResetPagination(false);
    const newFilterState = {
      ...filterState,
      start: paginationData.currentPageIndex * paginationData.pageLimit,
      count: paginationData.pageLimit,
    };

    setFilterState(newFilterState);
  };

  const onPartnerSelection = partner => {
    if (searchFilter !== 'emailId') {
      const newFilterState = {
        ...filterState,
        secondaryRoles: [decodeURI(partner.value)],
        start: 0,
        count: pageLimit,
      };

      setFilterState(newFilterState);
      setSelectedPartner(partner.label);
      setShowPartnerSuggestions(false);
      updateHistoryWithSelection('partner', partner, 'partnerName');
    }
  };

  const handleSearchClear = () => {
    const newFilterState = {
      ...filterState,
      secondaryRoles: [],
      start: 0,
      count: pageLimit,
    };

    if (selectedPartner || emailQuery) {
      setFilterState(newFilterState);
      setSelectedPartner('');
      setEmailQuery('');
    }
    history.push(`/permissions/users`);
  };

  const getSuggestions = () => {
    if (!showPartnerSuggestions || searchFilter === 'emailId') {
      return null;
    }
    return partnerSuggestions;
  };

  const handleFilter = async (event, option) => {
    if (searchFilter !== option.key) {
      const newSearchFilter = option.key;
      // Clears the search query when changing filter types
      const searchContainer = event.target.closest('[aria-label="filterAndSearchContainer"]');
      const searchClearBtn = searchContainer.querySelector('[aria-label="Clear text"]');
      if (searchClearBtn) {
        searchClearBtn.click();
      }
      setSearchFilter(newSearchFilter);
    }
  };

  const renderSearchContainer = () => {
    if (filterState.role === 'partner') {
      return (
        <div className={style.filterAndSearchContainer} aria-label="filterAndSearchContainer">
          <Dropdown
            ariaLabel="Dropdown"
            placeholder="Select"
            selectedKey={searchFilter}
            options={searchOptions}
            className={style.searchFilter}
            styles={{
              title: { backgroundColor: theme.palette.neutralWhite },
              dropdown: { fontWeight: 'normal' },
              dropdownItem: { fontWeight: 'normal' },
            }}
            onChange={(event, option) => {
              handleFilter(event, option);
            }}
          />
          <SearchBar
            isVisible={filterState.role === 'partner'}
            placeholder={
              searchOptions.find(option => option.key === searchFilter)?.placeholder || 'Search'
            }
            ariaLabel="Search"
            underlined
            name="partner-search"
            onClick={onPartnerSelection}
            value={selectedPartner || emailQuery}
            onChange={handleSearch}
            onClear={handleSearchClear}
            suggestions={getSuggestions()}
            isLoading={isLoading}
            data-testid="search-partner"
            className={style.search}
          />
        </div>
      );
    }

    return (
      <SearchBar
        placeholder={
          searchOptions.find(option => option.key === searchFilter)?.placeholder || 'Search'
        }
        ariaLabel="Search"
        underlined
        onChange={handleSearch}
        onClear={handleSearchClear}
        value={emailQuery}
        data-testid="search-msft"
        className={style.search}
      />
    );
  };

  return (
    <>
      <Module>
        <div className={style.searchSection}>
          <Dropdown
            name="users"
            label="Role"
            data-testid="role-selector"
            options={userFilterOptions}
            multiSelect={false}
            onChange={onRoleChange}
            className={style.filter}
            defaultValue="msft"
          />
          {renderSearchContainer()}
        </div>
        {(() => {
          const resultsCount =
            searchResults.length || (selectedPartner ? users.length : totalCount);
          const isListLoading = isSearchResultsLoading || isUsersLoading;

          if (isListLoading) {
            return (
              <UserList
                shimmered
                className={style.userList}
                enableShimmer
                items={[]}
                isPartner={filterState.role === 'partner'}
              />
            );
          }

          if ((emailQuery && !searchResults.length) || (selectedPartner && !users.length)) {
            return (
              <div className={style.resultsMessage}>
                <Text as="h4" block>
                  No Results
                </Text>
                <Text as="p" block className={style.noResultsSentence}>
                  {
                    userFilterOptions.find(option => option.role === filterState.role)
                      ?.noResultMessage
                  }
                </Text>
              </div>
            );
          }
          if (filterState.role === 'partner') {
            return (
              <>
                <div className={style.resultsMessage}>
                  <Text as="h4" block>
                    {resultsCount ? `${resultsCount} Results` : ''}
                  </Text>
                  {totalCount > pageLimit && (
                    <Text className={style.partnerResultsMessage} block>
                      **Only top {pageLimit} users are displayed here. Please use the Partner filter
                      to narrow down users for a specific partner.
                    </Text>
                  )}
                </div>
                <UserList
                  className={style.userList}
                  items={emailQuery ? searchResults : users}
                  isPartner
                />
              </>
            );
          }
          return (
            <>
              <div className={style.resultsMessage}>
                <Text as="h4" block>
                  {resultsCount ? `${resultsCount} Results` : ''}
                </Text>
              </div>
              <UserList className={style.userList} items={emailQuery ? searchResults : users} />
            </>
          );
        })()}
        {filterState.role === 'msft' && !emailQuery && (
          <Pagination
            totalRecords={totalCount}
            pageLimit={pageLimit}
            handlePageChange={handlePageChange}
            className={style.pagination}
            resetPagination={resetPagination}
          />
        )}
      </Module>
    </>
  );
};

export default SearchUsers;
